import React from "react";
import styled from "styled-components";

import { RendererProps } from "@jsonforms/core";
import { withJsonFormsLayoutProps } from "@jsonforms/react";
import { Categorization, UISchemaElement } from "@jsonforms/core/lib/models/uischema";
import { MaterialLayoutRenderer, MaterialLayoutRendererProps } from "@jsonforms/material-renderers";
import Tabs, { Tab, TabPanel } from "@ingka/tabs";
import { fontSize100, fontSize50, fontSize75 } from "@ingka/variables/design-tokens";

type CategorizationWithDepth = Categorization & { depth?: number };

const StyledTabPanel = styled(TabPanel)`
	padding: 1.5rem 0;
`;

const StyledTab = styled(Tab)`
	&[role="tab"] {
		&.depth-0 {
			font-size: ${fontSize100};
		}

		&.depth-2 {
			font-size: ${fontSize75};
		}

		font-size: ${fontSize50};
	}
`;

const LAYOUT_TYPES = new Set(["VerticalLayout", "HorizontalLayout", "Group", "Category"]);

const createTabPanelId = (depth: number, index: number) => `tabpanel-${depth}-${index}`;

const isLayout = (type?: string) => !!type && LAYOUT_TYPES.has(type);

const isControl = (type?: string) => type === "Control";

const isCategorization = (type?: string) => type === "Categorization";

const isCategory = (type?: string) => type === "Category";

const containsInteractiveControl = (uischema: UISchemaElement | undefined): boolean => {
	if (!uischema) {
		return false;
	}

	if (isControl(uischema.type)) {
		return uischema.options?.readOnly !== true;
	}

	if (isCategorization(uischema.type)) {
		return true;
	}

	if (isLayout(uischema.type)) {
		const children = (uischema as any).elements as UISchemaElement[] | undefined;
		if (!children) {
			return false;
		}
		return children.some((child) => containsInteractiveControl(child));
	}

	return false;
};

const categoryHasInteractiveElements = (category: UISchemaElement): boolean => {
	const elements = (category as any).elements as UISchemaElement[] | undefined;
	if (!elements || elements.length === 0) {
		return false;
	}
	return elements.some((el) => containsInteractiveControl(el));
};

const findFirstControlScope = (uischema: UISchemaElement | undefined): string | undefined => {
	if (!uischema) {
		return undefined;
	}

	if (isControl(uischema.type)) {
		return (uischema as any).scope;
	}

	if (isLayout(uischema.type) || isCategorization(uischema.type)) {
		const children = (uischema as any).elements as UISchemaElement[] | undefined;
		if (!children) {
			return undefined;
		}

		for (const child of children) {
			const scope = findFirstControlScope(child);
			if (scope) {
				return scope;
			}
		}
	}

	return undefined;
};

const getCategoryStableKey = (category: UISchemaElement, index: number): string => {
	const label = (category as any).label ?? `category-${index}`;
	const scope = findFirstControlScope(category) ?? "no-scope";
	return `${label}::${scope}`;
};

const CategoryLayout: React.FC<RendererProps> = ({ path, renderers, cells, schema, uischema, visible, enabled }) => {
	const categorization = uischema as CategorizationWithDepth;
	const depth = categorization.depth ?? 0;

	const tabsData = React.useMemo(
		() => (categorization.elements ?? []).filter((category) => isCategory(category.type)),
		[categorization.elements]
	);

	const tabKeys = React.useMemo(() => tabsData.map((tab, index) => getCategoryStableKey(tab, index)), [tabsData]);

	const hasFocusableByIndex = React.useMemo(
		() => tabsData.map((category) => categoryHasInteractiveElements(category)),
		[tabsData]
	);

	const storageKey = `category-tab-depth-${depth}`;
	const defaultTabKey = tabKeys[0] ?? "";

	const getInitialTabKey = React.useCallback(() => {
		if (typeof window === "undefined") {
			return defaultTabKey;
		}

		const saved = window.localStorage.getItem(storageKey);
		return saved && tabKeys.includes(saved) ? saved : defaultTabKey;
	}, [defaultTabKey, storageKey, tabKeys]);

	const [activeTabKey, setActiveTabKey] = React.useState<string>(getInitialTabKey);

	React.useEffect(() => {
		if (!tabKeys.includes(activeTabKey)) {
			setActiveTabKey(defaultTabKey);
		}
	}, [activeTabKey, defaultTabKey, tabKeys]);

	React.useEffect(() => {
		if (typeof window !== "undefined" && activeTabKey) {
			window.localStorage.setItem(storageKey, activeTabKey);
		}
	}, [activeTabKey, storageKey]);

	const activeCategoryIndex = React.useMemo(() => {
		const index = tabKeys.findIndex((key) => key === activeTabKey);
		return index >= 0 ? index : 0;
	}, [activeTabKey, tabKeys]);

	const childProps: MaterialLayoutRendererProps = React.useMemo(
		() => ({
			elements: tabsData[activeCategoryIndex]?.elements ?? [],
			schema,
			path,
			direction: "column",
			enabled,
			visible,
			renderers,
			cells,
		}),
		[activeCategoryIndex, cells, enabled, path, renderers, schema, tabsData, visible]
	);

	const handleActiveCategory = React.useCallback(
		(tabPanelId: string) => {
			const index = Number(tabPanelId.split("-").pop());

			if (!Number.isNaN(index) && tabKeys[index]) {
				setActiveTabKey(tabKeys[index]);
			}
		},
		[tabKeys]
	);

	const activeTabId = React.useMemo(() => createTabPanelId(depth, activeCategoryIndex), [depth, activeCategoryIndex]);

	const isActive = React.useCallback((index: number) => activeCategoryIndex === index, [activeCategoryIndex]);

	return (
		<Tabs
			activeTab={activeTabId}
			onTabChanged={handleActiveCategory}
			tabPanels={tabsData.map((_, index) => {
				const panelId = createTabPanelId(depth, index);
				const hasFocusable = hasFocusableByIndex[index];

				return (
					<StyledTabPanel key={panelId} tabPanelId={panelId} hasFocusableContent={hasFocusable}>
						{isActive(index) && <MaterialLayoutRenderer key={panelId} {...childProps} />}
					</StyledTabPanel>
				);
			})}
			tabs={tabsData.map((tab, index) => {
				const panelId = createTabPanelId(depth, index);

				return (
					<StyledTab
						className={`depth-${depth}`}
						key={`tab-${panelId}`}
						id={`tab-${panelId}`}
						tabPanelId={panelId}
						text={(tab as any).label}
					/>
				);
			})}
		/>
	);
};

export default withJsonFormsLayoutProps(CategoryLayout);
